import React, { useCallback, useMemo, useState, type MouseEvent } from "react";
import { Pin } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  useChannelActionContext,
  useChannelStateContext,
} from "stream-chat-react";
import { toast } from "sonner";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const getTimestamp = (value: Date | string | null | undefined) => {
  if (!value) return 0;

  const timestamp =
    value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const formatDate = (value: Date | string | null | undefined) => {
  const timestamp = getTimestamp(value);
  return timestamp ? new Date(timestamp).toLocaleString() : "";
};

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

export function PinnedMessagesDialog() {
  const { t } = useTranslation();
  const { pinnedMessages = [] } = useChannelStateContext();
  const { jumpToMessage } = useChannelActionContext();
  const [open, setOpen] = useState(false);

  const sortedPinnedMessages = useMemo(
    () =>
      [...pinnedMessages].sort(
        (first, second) =>
          getTimestamp(second.pinned_at) - getTimestamp(first.pinned_at),
      ),
    [pinnedMessages],
  );

  const handlePinnedMessageClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const messageId = event.currentTarget.dataset.messageId;
      if (!messageId) return;

      setOpen(false);
      window.requestAnimationFrame(() => {
        jumpToMessage(messageId, undefined, 1000).catch(() => {
          toast.error(t("chats.pinsJumpError"));
        });
      });
    },
    [jumpToMessage, t],
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Pin className="size-4" />
          {t("chats.pins")}
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-lg gap-4">
        <DialogHeader>
          <DialogTitle>{t("chats.pinnedMessages")}</DialogTitle>
          <DialogDescription>
            {t("chats.pinnedMessagesDescription")}
          </DialogDescription>
        </DialogHeader>

        {sortedPinnedMessages.length === 0 ? (
          <div className="text-muted-foreground rounded-md border border-dashed px-4 py-10 text-center text-sm">
            {t("chats.noPinnedMessages")}
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto pr-3">
            <div className="flex flex-col gap-2">
              {sortedPinnedMessages.map((message) => {
                const authorName =
                  message.user?.name || t("chats.unknownMessageAuthor");
                const messagePreview =
                  message.text?.trim() ||
                  (message.attachments?.length
                    ? t("chats.pinnedAttachment")
                    : t("chats.emptyPinnedMessage"));

                return (
                  <button
                    key={message.id}
                    type="button"
                    data-message-id={message.id}
                    onClick={handlePinnedMessageClick}
                    className="hover:bg-accent focus-visible:ring-ring flex w-full gap-3 rounded-md border p-3 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none"
                  >
                    <Avatar className="size-9 shrink-0">
                      <AvatarImage src={message.user?.image} alt={authorName} />
                      <AvatarFallback>{getInitials(authorName)}</AvatarFallback>
                    </Avatar>

                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline justify-between gap-3">
                        <span className="truncate text-sm font-medium">
                          {authorName}
                        </span>
                        <span className="text-muted-foreground shrink-0 text-xs">
                          {formatDate(message.created_at)}
                        </span>
                      </span>
                      <span className="text-muted-foreground mt-1 line-clamp-3 block text-sm break-words whitespace-pre-wrap">
                        {messagePreview}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
